/**
 * URL-backed scenario state.
 *
 * The whole calculator is stateless server-side (project rule: no backend
 * database, no server-side storage of user input — see AGENTS.md). Scenario
 * state lives entirely in the query string, so any run is shareable,
 * reproducible, and permanent without our infrastructure holding a byte of
 * PII.
 *
 * Encoding contract:
 * - Every input has a short two- or three-character parameter name (`r` for
 *   monthly rent, `hp` for home price, etc.). Short names keep the URL under
 *   a couple hundred characters when the user has customised every field.
 * - Only *overrides* are written to the URL. If a field still equals the
 *   default, its parameter is omitted. This keeps the base URL clean and
 *   makes shared URLs self-documenting: the parameters that appear are
 *   exactly what the sender changed.
 * - Unknown parameters and malformed numeric values are ignored — the parser
 *   falls back to the default rather than throwing. This preserves the
 *   invariant that any URL renders a valid scenario.
 */

import {
  SCENARIO_DEFAULTS,
  makeDefaultScenario,
  type NumericScenarioKey,
  type ScenarioInputs,
} from "./defaults";

const NUMERIC_PARAMS: Record<NumericScenarioKey, string> = {
  monthlyRent: "r",
  homePrice: "hp",
  downPaymentPercent: "dp",
  annualInterestRatePercent: "mr",
  horizonYears: "hz",
  annualHomeAppreciationPercent: "ha",
  termYears: "tm",
  annualRentInflationPercent: "ri",
  annualDiscountRatePercent: "dr",
  annualPropertyTaxRatePercent: "pt",
  annualHomeInsurance: "hi",
  annualMaintenanceRatePercent: "mn",
  monthlyHOA: "ho",
  annualRentersInsurance: "rn",
};

const JURISDICTION_PARAM = "j";
const NEVER_SELL_PARAM = "ns";

/**
 * Parse a query string (with or without leading `?`) into a fully-populated
 * scenario. Missing or invalid values fall back to defaults.
 */
export function parseScenarioFromQuery(query: string): ScenarioInputs {
  const scenario = makeDefaultScenario();
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);

  for (const [key, param] of Object.entries(NUMERIC_PARAMS) as [
    NumericScenarioKey,
    string,
  ][]) {
    const raw = params.get(param);
    if (raw === null) continue;
    const value = Number(raw);
    if (!Number.isFinite(value)) continue;
    scenario[key] = value;
  }

  const jurisdictionId = params.get(JURISDICTION_PARAM);
  if (jurisdictionId !== null && jurisdictionId.length > 0) {
    scenario.jurisdictionId = jurisdictionId;
  }

  const neverSell = params.get(NEVER_SELL_PARAM);
  if (neverSell !== null) {
    scenario.neverSell = neverSell === "1" || neverSell.toLowerCase() === "true";
  }

  return scenario;
}

/**
 * Encode a scenario as a query string of *overrides only* — parameters equal
 * to their default are omitted. Empty result means "no overrides"; the caller
 * should render a bare URL in that case.
 */
export function encodeScenarioToQuery(scenario: ScenarioInputs): string {
  const params = new URLSearchParams();

  for (const [key, param] of Object.entries(NUMERIC_PARAMS) as [
    NumericScenarioKey,
    string,
  ][]) {
    const current = scenario[key];
    const fallback = SCENARIO_DEFAULTS[key];
    if (!numericEquals(current, fallback)) {
      params.set(param, formatForQuery(current));
    }
  }

  if (scenario.jurisdictionId !== SCENARIO_DEFAULTS.jurisdictionId) {
    params.set(JURISDICTION_PARAM, scenario.jurisdictionId);
  }

  if (scenario.neverSell !== SCENARIO_DEFAULTS.neverSell) {
    params.set(NEVER_SELL_PARAM, scenario.neverSell ? "1" : "0");
  }

  return params.toString();
}

/**
 * Compare two numeric values with a tolerance small enough to treat
 * "3.5" and "3.5000000001" as equal, but large enough not to swallow
 * meaningful cent-level differences.
 */
function numericEquals(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

/**
 * Format a number for query-string embedding. Uses a fixed max of 4
 * decimal places, trimming trailing zeros so integers and simple
 * percentages look natural in a URL bar.
 */
function formatForQuery(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const rounded = Math.round(value * 10_000) / 10_000;
  return String(rounded);
}
